import React, { useState, useEffect } from 'react';
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import LoadingOverlay from '../loading/loading';
import service from '../../services/service';

const UserTransactionsList = () => {
    const push = useNavigate();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    useEffect(() => {
        fetchTransactions();
    }, []);

    const fetchTransactions = async () => {
        try {
            setLoading(true);
            const response = await service.save(
                { userId: sessionStorage.getItem("userId") }, 
                `wallet/transactions`, 
                "POST"
            );
            if (response && !response.success) {
                toast.error(response.message);
                return;
            }
            setData(response.data);
        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    const filteredData = data.filter(user => {
        const fullName = `${user.userId?.firstName || ''} ${user.userId?.lastName || ''}`.toLowerCase();
        return (
            fullName.includes(searchTerm.toLowerCase()) ||
            user.userId?.contact_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.balance.toString().includes(searchTerm)
        );
    });

    const totalPages = Math.ceil(filteredData.length / itemsPerPage);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const columns = [
        { key: 'firstName', label: 'Name ', sortable: true },
        { key: 'contact_number', label: 'Contact Number ', sortable: true },
        { key: 'balance', label: 'Current Balance ', sortable: true },
    ];

    const styles = {
        actionIcon: {
            padding: '6px 8px',
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            borderRadius: '4px',
            color: '#6b7280',
            transition: 'all 0.2s',
            fontSize: '16px',
            lineHeight: 1,
        },
        container: {
            width: '100%',
            maxWidth: '1400px',
            margin: '0 auto',
            padding: '16px',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        },
        header: {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
            flexWrap: 'wrap',
            gap: '12px',
        },
        title: {
            fontSize: '20px',
            fontWeight: '600',
            color: '#111827',
            margin: '0',
        },
        searchContainer: {
            position: 'relative',
            width: '100%',
            maxWidth: '320px',
            display: 'flex',
            alignItems: 'center',
        },
        searchInput: {
            width: '100%',
            padding: '8px 12px 8px 36px',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            fontSize: '14px',
            outline: 'none',
            transition: 'all 0.2s',
        },
        tableContainer: {
            overflowX: 'auto',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
        },
        table: {
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '14px',
            textAlign: 'left',
        },
        th: {
            padding: '12px 16px',
            backgroundColor: '#f9fafb',
            borderBottom: '1px solid #e5e7eb',
            color: '#374151',
            fontWeight: '600',
            cursor: 'pointer',
            userSelect: 'none',
            whiteSpace: 'nowrap',
        },
        td: {
            padding: '12px 16px',
            borderBottom: '1px solid #e5e7eb',
            color: '#374151',
        },
        pagination: {
            marginTop: '16px',
            padding: '8px 12px',
            backgroundColor: '#f9fafb',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
        },
        paginationInfo: {
            color: '#6b7280',
            fontSize: '12px',
            fontWeight: '500',
            textAlign: 'center',
        },
        paginationControls: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            flexWrap: 'wrap',
        },
        pageButton: {
            minWidth: '32px',
            height: '32px',
            padding: '0 8px',
            backgroundColor: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '12px',
            color: '#374151',
            transition: 'all 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            ':hover': {
                backgroundColor: '#f3f4f6',
            }
        },
        activePageButton: {
            minWidth: '32px',
            height: '32px',
            padding: '0 8px',
            backgroundColor: '#4CAF50',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '12px',
            color: 'white',
            transition: 'all 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            ':hover': {
                backgroundColor: '#45a049',
            }
        },
        disabledButton: {
            minWidth: '32px',
            height: '32px',
            padding: '0 8px',
            backgroundColor: '#f3f4f6',
            border: '1px solid #e5e7eb',
            borderRadius: '6px',
            cursor: 'not-allowed',
            fontSize: '12px',
            color: '#9ca3af',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
        ellipsis: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '32px',
            height: '32px',
            color: '#6b7280',
            fontSize: '12px',
        },
        icon: {
            fontSize: '14px',
            display: 'inline-block',
            cursor: 'pointer',
            userSelect: 'none',
        },
        searchIcon: {
            position: 'absolute',
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#6b7280',
            fontSize: '14px',
        },
        serialNo: {
            width: '60px',
            textAlign: 'center',
            color: '#6b7280',
            fontWeight: '500',
        },
        // Media query styles
        '@media (max-width: 768px)': {
            container: {
                padding: '12px',
            },
            header: {
                flexDirection: 'column',
                alignItems: 'flex-start',
            },
            searchContainer: {
                maxWidth: '100%',
            },
            th: {
                padding: '8px 12px',
            },
            td: {
                padding: '8px 12px',
            },
            pagination: {
                padding: '8px',
            },
        },
        '@media (max-width: 480px)': {
            title: {
                fontSize: '18px',
            },
            table: {
                fontSize: '12px',
            },
            pageButton: {
                minWidth: '28px',
                height: '28px',
                fontSize: '11px',
            },
            activePageButton: {
                minWidth: '28px',
                height: '28px',
                fontSize: '11px',
            },
            disabledButton: {
                minWidth: '28px',
                height: '28px',
                fontSize: '11px',
            },
            ellipsis: {
                width: '28px',
                height: '28px',
                fontSize: '11px',
            },
        }
    };

    const icons = {
        search: '🔍',
        edit: '✏️',
        delete: '🗑️',
        block: '🚫',
        unblock: '🔓',
        more: '⋮',
        check: '✓',
        cross: '✕',
        sortUp: '▲',
        sortDown: '▼',
        sortBoth: '⇵',
        view: '📄',
        prev: '◄',
        next: '►'
    };

    const handleActionButton = (type, id) => {
        try {
            if (type === 'View') {
                push(`/admin/wallet/transactions/${id}`);
            }
        } catch (error) {
            toast.error(error.message);
        }
    };

    const renderPaginationButtons = () => {
        const buttons = [];
        const maxButtons = window.innerWidth <= 480 ? 3 : 5; // Fewer buttons on mobile

        if (totalPages <= maxButtons) {
            for (let i = 1; i <= totalPages; i++) {
                buttons.push(
                    <button
                        key={i}
                        style={i === currentPage ? styles.activePageButton : styles.pageButton}
                        onClick={() => handlePageChange(i)}
                    >
                        {i}
                    </button>
                );
            }
        } else {
            const startPage = Math.max(1, currentPage - Math.floor(maxButtons / 2));
            const endPage = Math.min(totalPages, startPage + maxButtons - 1);

            if (startPage > 1) {
                buttons.push(
                    <button
                        key={1}
                        style={styles.pageButton}
                        onClick={() => handlePageChange(1)}
                    >
                        1
                    </button>
                );
                if (startPage > 2) {
                    buttons.push(<span key="start-ellipsis" style={styles.ellipsis}>...</span>);
                }
            }

            for (let i = startPage; i <= endPage; i++) {
                buttons.push(
                    <button
                        key={i}
                        style={i === currentPage ? styles.activePageButton : styles.pageButton}
                        onClick={() => handlePageChange(i)}
                    >
                        {i}
                    </button>
                );
            }

            if (endPage < totalPages) {
                if (endPage < totalPages - 1) {
                    buttons.push(<span key="end-ellipsis" style={styles.ellipsis}>...</span>);
                }
                buttons.push(
                    <button
                        key={totalPages}
                        style={styles.pageButton}
                        onClick={() => handlePageChange(totalPages)}
                    >
                        {totalPages}
                    </button>
                );
            }
        }

        return buttons;
    };

    return (
        <>
            {loading && <LoadingOverlay />}
            <div style={styles.container}>
                <div style={styles.header}>
                    <h2 style={styles.title}>User Transactions</h2>
                    <div style={styles.searchContainer}>
                        <span style={styles.searchIcon}>{icons.search}</span>
                        <input
                            style={styles.searchInput}
                            type="text"
                            placeholder="Search by name, contact, or balance..."
                            value={searchTerm}
                            onChange={handleSearch}
                        />
                    </div>
                </div>
                <div style={styles.tableContainer}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                {columns.map((column) => (
                                    <th
                                        key={column.key}
                                        style={{
                                            ...styles.th,
                                            cursor: column.sortable ? 'pointer' : 'default'
                                        }}
                                    >
                                        {column.label}
                                    </th>
                                ))}
                                <th style={{ ...styles.th }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentItems.map((user) => (
                                <tr key={user.userId._id}>
                                    <td style={styles.td}>
                                        <span className="text-primary">
                                            {user.userId?.firstName || '-'} {user.userId?.lastName || ''}
                                        </span>
                                    </td>
                                    <td style={styles.td}>{user.userId?.contact_number || '-'}</td>
                                    <td style={styles.td}>
                                        <span className="text-success">
                                            ₹ {user?.balance.toFixed(2) || '-'}
                                        </span>
                                    </td>
                                    <td style={styles.td}>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <button
                                                style={styles.actionIcon}
                                                title="View User Transactions"
                                                onClick={() => handleActionButton('View', user.userId._id)}
                                            >
                                                {icons.view}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {currentItems.length === 0 && (
                                <tr>
                                    <td colSpan={columns.length + 1} style={{ ...styles.td, textAlign: 'center' }}>
                                        No matching records found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                {filteredData.length > 0 && (
                    <div style={styles.pagination}>
                        <div style={styles.paginationInfo}>
                            Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredData.length)} of {filteredData.length} entries
                        </div>
                        <div style={styles.paginationControls}>
                            <button
                                style={currentPage === 1 ? styles.disabledButton : styles.pageButton}
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                            >
                                {icons.prev}
                            </button>
                            {renderPaginationButtons()}
                            <button
                                style={currentPage === totalPages ? styles.disabledButton : styles.pageButton}
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                            >
                                {icons.next}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default UserTransactionsList;